// rewardsApi — adaptador de Recompensas del manager (Inc8) sobre lealify-base.
// Reemplaza las escrituras Firestore de la colección `rewards` + el historial de canjes
// que daba Elasticsearch (searchRedemptionsHistory). Mapea al shape Reward que ya
// consumen RewardsManagementPage/RewardsList/RewardForm.
import { baseApi } from './baseClient';
import type { Reward, RewardFormData } from '../features/rewards/types/reward';

// Fila de la tabla `rewards` de base → objeto Reward del panel.
export function mapRewardRow(r: any): Reward {
  return {
    id: String(r.id),
    title: r.title || '',
    description: r.description || '',
    points: Number(r.points) || 0,
    imageUrl: r.image_url || '',
    isActive: r.status === 'active',
    businessId: '',
    createdAt: r.created_at ? new Date(r.created_at) : new Date(),
    updatedAt: r.updated_at ? new Date(r.updated_at) : new Date(),
    deletedAt: r.deleted_at ? new Date(r.deleted_at) : null,
    status: r.status,
    branchIds: Array.isArray(r.branch_ids) ? r.branch_ids.map(String) : [],
    branchNames: Array.isArray(r.branch_names) ? r.branch_names : [],
  };
}

// Item del historial de canjes de una recompensa (shape que esperaba la vista ES).
export interface RewardRedemption {
  id: string;
  visitorName: string;
  visitorPhone: string;
  redeemedAt: string;
  points: number;
  branchName?: string;
  userName?: string;   // quién hizo el canje (usuario del panel/kiosco)
}

// Item del historial GLOBAL de canjes del negocio (incluye el título de la recompensa).
export interface RedemptionHistoryItem extends RewardRedemption {
  rewardTitle: string;
  // 28-sep-2026: estado para el botón «Devolver puntos».
  status: 'completed' | 'reversed';
  posTracked: boolean;        // el negocio tiene caja: el canje dejó cupón para el POS
  posConsumedAt: string | null; // la caja lo cobró (entonces NO se puede devolver)
  posTicket: string | null;
}

const fromForm = (data: RewardFormData) => ({
  title: data.title,
  description: data.description || null,
  points: data.points,
  image_url: data.imageUrl || null,
  status: data.isActive ? 'active' : 'inactive',
  ...(data.branchIds ? { branch_ids: data.branchIds.map(Number) } : {}),
});

export const rewardsApi = {
  // Listado (incluye inactivas, excluye borradas) → para la gestión del catálogo.
  list: async (): Promise<Reward[]> => {
    const r = await baseApi.get<{ rewards: any[] }>('/loyalty/rewards?all=1');
    return (r.rewards || []).map(mapRewardRow);
  },

  create: (data: RewardFormData) => baseApi.post<{ id: number }>('/loyalty/rewards', fromForm(data)),

  update: (id: string, data: RewardFormData) => baseApi.put(`/loyalty/rewards/${id}`, fromForm(data)),

  // Soft-delete (status='deleted' server-side).
  remove: (id: string) => baseApi.del(`/loyalty/rewards/${id}`),

  // Historial de canjes de una recompensa (paginado + búsqueda por nombre/teléfono).
  redemptions: async (rewardId: string, term = '', page = 1, size = 50): Promise<{ items: RewardRedemption[]; total: number }> => {
    const p = new URLSearchParams();
    if (term) p.set('term', term);
    p.set('page', String(page));
    p.set('size', String(size));
    const r = await baseApi.get<{ items: any[]; total: number }>(`/loyalty/rewards/${rewardId}/redemptions?${p}`);
    const items: RewardRedemption[] = (r.items || []).map((x) => ({
      id: String(x.id),
      visitorName: x.visitor_name || 'Cliente',
      visitorPhone: String(x.visitor_phone || ''),
      redeemedAt: x.redeemed_at ? String(x.redeemed_at) : '',
      points: Number(x.points) || 0,
      branchName: x.branch_name || undefined,
      userName: x.user_name || undefined,
    }));
    return { items, total: r.total || 0 };
  },

  // IDs de contacto ÚNICOS que canjearon una recompensa (para etiquetado masivo del TagManager).
  // Pagina el historial de canjes de la recompensa y deduplica por contact_id. El endpoint
  // /loyalty/rewards/:id/redemptions ya proyecta contact_id.
  redemptionContactIds: async (rewardId: string): Promise<string[]> => {
    const ids = new Set<string>();
    const size = 200;
    for (let page = 1; page <= 100; page++) {
      const p = new URLSearchParams();
      p.set('page', String(page));
      p.set('size', String(size));
      const r = await baseApi.get<{ items: any[]; total: number }>(`/loyalty/rewards/${rewardId}/redemptions?${p}`);
      const items = r.items || [];
      for (const x of items) if (x.contact_id != null) ids.add(String(x.contact_id));
      if (items.length < size) break;
    }
    return [...ids];
  },

  // Historial GLOBAL de canjes del negocio (todas las recompensas) → para la página "Canjear Recompensas".
  history: async (term = '', page = 1, size = 10): Promise<{ items: RedemptionHistoryItem[]; total: number }> => {
    const p = new URLSearchParams();
    if (term) p.set('term', term);
    p.set('page', String(page));
    p.set('size', String(size));
    const r = await baseApi.get<{ items: any[]; total: number }>(`/loyalty/redemptions?${p}`);
    const items: RedemptionHistoryItem[] = (r.items || []).map((x) => ({
      id: String(x.id),
      visitorName: x.visitor_name || 'Cliente',
      visitorPhone: String(x.visitor_phone || ''),
      rewardTitle: x.reward_title || 'Recompensa',
      points: Number(x.points) || 0,
      redeemedAt: x.redeemed_at ? String(x.redeemed_at) : '',
      branchName: x.branch_name || undefined,
      userName: x.user_name || undefined,
      status: x.status === 'reversed' ? 'reversed' : 'completed',
      posTracked: Number(x.pos_tokens) > 0,
      posConsumedAt: x.pos_consumed_at ? String(x.pos_consumed_at) : null,
      posTicket: x.pos_ticket ? String(x.pos_ticket) : null,
    }));
    return { items, total: r.total || 0 };
  },

  // Devuelve los puntos de un canje que no se entregó (permiso manage). El servidor lo rechaza
  // si la caja ya lo cobró; si el cupón seguía vivo en la caja, lo apaga.
  reverse: async (redemptionId: string): Promise<{ points_returned: number; balance: number }> => {
    return baseApi.post(`/loyalty/redemptions/${redemptionId}/reverse`, {});
  },
};
